import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../context/authContext';
import { toast } from 'react-toastify';
import { Modal } from '../components/ui/Modal';

const ROLES_DISPONIBLES = [
    { value: 'contable', label: 'Contable' },
    { value: 'seguimiento', label: 'Seguimiento' },
    { value: 'legal', label: 'Legal' },
];

// Página (solo admin) para gestionar las notificaciones enviadas:
// tabla con la notificación y su detalle de lectura (quiénes leyeron y
// quiénes no), y la posibilidad de reenviar la misma notificación.
export const GestionNotificacionesPage = () => {
  const { token } = useContext(AuthContext);
  const [notificaciones, setNotificaciones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandida, setExpandida] = useState(null);
  const [reenviandoId, setReenviandoId] = useState(null);

  // Modal de reenvío
  const [reenviarModal, setReenviarModal] = useState(null); // null o la notificación seleccionada
  const [reenviarTarget, setReenviarTarget] = useState('todos');
  const [reenviarRoles, setReenviarRoles] = useState([]);

  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const cargar = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/notificaciones-usuario/gestion', { headers });
      if (!res.ok) throw new Error('Error al cargar las notificaciones');
      const data = await res.json();
      setNotificaciones(data?.data || []);
    } catch (err) {
      console.error('Error al cargar gestión de notificaciones:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const abrirModalReenviar = (notif) => {
    setReenviarModal(notif);
    setReenviarTarget('todos');
    setReenviarRoles([]);
  };

  const toggleReenviarRol = (value) => {
    setReenviarRoles(prev => prev.includes(value) ? prev.filter(r => r !== value) : [...prev, value]);
  };

  const handleConfirmReenviar = async () => {
    if (!reenviarModal || reenviandoId !== null) return;
    if (reenviarTarget === 'roles' && reenviarRoles.length === 0) {
      toast.warn('Seleccioná al menos un rol destino');
      return;
    }
    setReenviandoId(reenviarModal.id);
    setError('');
    try {
      const res = await fetch(`/api/notificaciones-usuario/${reenviarModal.id}/reenviar`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          target: reenviarTarget,
          roles: reenviarTarget === 'roles' ? reenviarRoles : undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || 'Error al reenviar');
      toast.success(`Notificación reenviada a ${data?.data?.destinatarios} usuario(s)`);
      setReenviarModal(null);
      cargar();
    } catch (err) {
      console.error('Error al reenviar:', err);
      toast.error(err.message || 'Error al reenviar la notificación');
    } finally {
      setReenviandoId(null);
    }
  };

  const toggleExpandir = (id) => {
    setExpandida(prev => (prev === id ? null : id));
  };

  const formatearFecha = (f) => {
    try {
      return new Date(f).toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' });
    } catch {
      return f;
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">Gestión de Notificaciones</h1>
        <button
          onClick={cargar}
          className="px-4 py-2 text-sm bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
        >
          Refrescar
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-md text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {loading && <p className="text-sm text-gray-500 dark:text-gray-400">Cargando...</p>}

      {!loading && notificaciones.length === 0 && (
        <p className="text-sm text-gray-500 dark:text-gray-400">No hay notificaciones enviadas.</p>
      )}

      {!loading && notificaciones.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="w-full text-sm table-fixed">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr className="text-left text-gray-600 dark:text-gray-300">
                <th className="p-3 font-medium w-2/5">Notificación</th>
                <th className="p-3 font-medium w-1/5">Fecha</th>
                <th className="p-3 font-medium text-center w-1/10">Destinatarios</th>
                <th className="p-3 font-medium text-center w-1/10">Leídas</th>
                <th className="p-3 font-medium text-center w-1/10">Sin leer</th>
                <th className="p-3 font-medium text-center w-1/10">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800 bg-white dark:bg-gray-900">
              {notificaciones.map(n => {
                const expandidaEsta = expandida === n.id;
                return (
                  <React.Fragment key={n.id}>
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-800">
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-800 dark:text-gray-100">{n.titulo}</span>
                          {n.imagen && <span className="text-xs text-gray-400">🖼</span>}
                        </div>
                      </td>
                      <td className="p-3 text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {formatearFecha(n.fecha_creacion)}
                      </td>
                      <td className="p-3 text-center text-gray-700 dark:text-gray-300">
                        {n.total_destinatarios}
                      </td>
                      <td className="p-3 text-center text-green-600 dark:text-green-400 font-medium">
                        {n.leidas}
                      </td>
                      <td className="p-3 text-center text-red-600 dark:text-red-400 font-medium">
                        {n.no_leidas}
                      </td>
                      <td className="p-3 text-center">
                        <div className="flex items-center justify-center gap-2">
                          {/* Reenviar */}
                          <button
                            onClick={() => abrirModalReenviar(n)}
                            disabled={reenviandoId !== null}
                            className="px-3 py-1 text-xs bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50 transition-colors"
                            title="Reenviar esta notificación"
                          >
                            Reenviar
                          </button>

                          {/* Icono colapsar/expandir */}
                          <button
                            onClick={() => toggleExpandir(n.id)}
                            className="p-1.5 rounded-md text-gray-500 hover:bg-gray-200 dark:text-gray-400 dark:hover:bg-gray-700 transition-colors"
                            title={expandidaEsta ? 'Contraer detalle' : 'Expandir detalle'}
                          >
                            <svg
                              className={`w-4 h-4 transition-transform ${expandidaEsta ? 'rotate-180' : ''}`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>

                    {expandidaEsta && (
                      <tr className="bg-gray-50/50 dark:bg-gray-800/50">
                        <td colSpan={6} className="p-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Contenido */}
                            <div>
                              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">Contenido</h4>
                              <p className="text-xs text-gray-600 dark:text-gray-300 whitespace-pre-wrap">
                                {n.descripcion || 'Sin descripción'}
                              </p>
                              {n.imagen && (
                                <img
                                  src={n.imagen}
                                  alt={n.titulo}
                                  className="mt-2 max-h-40 rounded-md border border-gray-200 dark:border-gray-700"
                                />
                              )}
                              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                                Destino: {n.target === 'todos' ? 'Todos los usuarios' : `Roles: ${(n.roles || []).join(', ')}`}
                              </p>
                            </div>

                            {/* Detalle de lectura por usuario */}
                            <div>
                              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">
                                Detalle de lectura
                              </h4>
                              {n.detalle && n.detalle.length > 0 ? (
                                <ul className="divide-y divide-gray-100 dark:divide-gray-800 border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden">
                                  {n.detalle.map(u => (
                                    <li key={u.user_id} className="flex items-center justify-between p-2 bg-white dark:bg-gray-900">
                                      <div>
                                        <p className="text-xs font-medium text-gray-800 dark:text-gray-100">{u.name}</p>
                                        <p className="text-xs text-gray-400 dark:text-gray-500">{u.role}</p>
                                      </div>
                                      <span
                                        className={`text-xs px-2 py-1 rounded-full font-medium ${
                                          u.leida
                                            ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                                            : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                                        }`}
                                      >
                                        {u.leida ? 'Leída ✓' : 'Sin leer ✗'}
                                      </span>
                                    </li>
                                  ))}
                                </ul>
                              ) : (
                                <p className="text-xs text-gray-400">Sin destinatarios.</p>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Modal de reenvío ── */}
      <Modal
        isOpen={reenviarModal !== null}
        onClose={() => setReenviarModal(null)}
        title="Reenviar notificación"
      >
        {reenviarModal && (
          <div className="space-y-4">
            {/* Contenido de la notificación */}
            <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-md border border-gray-200 dark:border-gray-700">
              <p className="font-semibold text-gray-800 dark:text-gray-100">{reenviarModal.titulo}</p>
              {reenviarModal.descripcion && (
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-1 whitespace-pre-wrap">
                  {reenviarModal.descripcion}
                </p>
              )}
              {reenviarModal.imagen && (
                <img
                  src={reenviarModal.imagen}
                  alt={reenviarModal.titulo}
                  className="mt-2 max-h-40 rounded-md border border-gray-200 dark:border-gray-700"
                />
              )}
            </div>

            {/* Selector de destinatarios */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Enviar a
              </label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <input
                    type="radio"
                    name="reenviar-target"
                    checked={reenviarTarget === 'todos'}
                    onChange={() => setReenviarTarget('todos')}
                  />
                  Todos los usuarios
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <input
                    type="radio"
                    name="reenviar-target"
                    checked={reenviarTarget === 'roles'}
                    onChange={() => setReenviarTarget('roles')}
                  />
                  Solo roles específicos
                </label>
              </div>

              {reenviarTarget === 'roles' && (
                <div className="flex gap-4 mt-2 ml-1">
                  {ROLES_DISPONIBLES.map(r => (
                    <label key={r.value} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                      <input
                        type="checkbox"
                        checked={reenviarRoles.includes(r.value)}
                        onChange={() => toggleReenviarRol(r.value)}
                      />
                      {r.label}
                    </label>
                  ))}
                </div>
              )}
            </div>

            <p className="text-xs text-gray-400 dark:text-gray-500">
              Se marcará como no leída para que los destinatarios la vuelvan a ver.
            </p>

            {/* Acciones */}
            <div className="flex justify-end gap-3 pt-2 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setReenviarModal(null)}
                className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700 rounded-md transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmReenviar}
                disabled={reenviandoId !== null}
                className="px-4 py-2 text-sm bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50 transition-colors"
              >
                {reenviandoId !== null ? 'Reenviando...' : 'Reenviar'}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};
